import { Box } from "@mui/material";
import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import HomeIcon from "@mui/icons-material/Home";
import { useBreadcrumbs } from "../../context/BreadcrumbContext";

// A record id in the address (the Knowledge Base's rows and extractions) is never shown as a crumb.
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function BasicBreadcrumbs() {
  const location = useLocation();
  const navigate = useNavigate();
  const { breadcrumbs, clearFromLevel, getDisplayName, setSelectedItem } = useBreadcrumbs();
  const [hoveredLink, setHoveredLink] = useState(null);

  const pathnames = location.pathname.split("/").filter((x) => x);

  if (pathnames.length === 0) return null;

  // ── Logistics breadcrumb support: start ─────────────────────────────
  const isLogisticsRoute = location.pathname === "/logistics";

  // Filter out numeric IDs and known ID parameter names
  const displayPathnames = pathnames.filter(segment =>
    !['siteid', 'plantid', 'assetid', 'subassetid', 'componentid', 'instrumentid', 'projectid', 'projectType', 'open'].includes(segment.toLowerCase()) &&
    !segment.match(/^\d+$/) && // Hide numeric IDs
    !UUID.test(segment)
  );

  // Use the context's getDisplayName function
  const labelMap = {
    sites: getDisplayName("site")
      ? getDisplayName("site").toUpperCase()   // ⬅ Make SITE uppercase
      : "Sites",
    plants: getDisplayName('plant') ? getDisplayName('plant').toUpperCase() : "Plants",
    assets: getDisplayName('asset') ? getDisplayName('asset').toUpperCase() : "Assets",
    "sub-asset": getDisplayName('subAsset') ? getDisplayName('subAsset').toUpperCase() : "Sub-assets",
    components: getDisplayName('component') ? getDisplayName('component').toUpperCase() : "Components",
    "project-dashboard": getDisplayName('project')
      ? `${getDisplayName('project')}-Dashboard`
      : "Project-Dashboard",
    "design-engineering-docs": "DESIGN-ENGINEERING-DOCS",
    "faults-diagnostics": "FAULTS-DIAGNOSTICS",
    knowledge: "KNOWLEDGE BASE",
    extract: "EXTRACT",
    continue: "CONTINUE",
    "maintenance-guide": "MAINTENANCE GUIDE",
  };

  const buildLogisticsRoute = (level) => {
    const params = new URLSearchParams();

    if (
      ["site", "plant", "asset", "subAsset", "component", "instrument"]
        .includes(level) &&
      breadcrumbs.site?.id
    ) {
      params.set("siteId", breadcrumbs.site.id);
    }

    if (
      ["plant", "asset", "subAsset", "component", "instrument"].includes(
        level,
      ) &&
      breadcrumbs.plant?.id
    ) {
      params.set("plantId", breadcrumbs.plant.id);
    }

    if (
      ["asset", "subAsset", "component", "instrument"].includes(level) &&
      breadcrumbs.asset?.id
    ) {
      params.set("assetId", breadcrumbs.asset.id);
    }

    if (
      ["subAsset", "component", "instrument"].includes(level) &&
      breadcrumbs.subAsset?.id
    ) {
      params.set("tierId", breadcrumbs.subAsset.id);
    }

    if (
      ["component", "instrument"].includes(level) &&
      breadcrumbs.component?.id
    ) {
      params.set("componentId", breadcrumbs.component.id);
    }

    if (level === "instrument" && breadcrumbs.instrument?.id) {
      params.set("instrumentId", breadcrumbs.instrument.id);
    }

    const query = params.toString();
    return query ? `/logistics?${query}` : "/logistics";
  };

  const logisticsTrail = [
    { level: "logistics", label: "LOGISTICS" },
    breadcrumbs.site && {
      level: "site",
      label: getDisplayName("site")?.toUpperCase(),
    },
    breadcrumbs.plant && {
      level: "plant",
      label: getDisplayName("plant")?.toUpperCase(),
    },
    breadcrumbs.asset && {
      level: "asset",
      label: getDisplayName("asset")?.toUpperCase(),
    },
    breadcrumbs.subAsset && {
      level: "subAsset",
      label: getDisplayName("subAsset")?.toUpperCase(),
    },
    breadcrumbs.component && {
      level: "component",
      label: getDisplayName("component")?.toUpperCase(),
    },
    breadcrumbs.instrument && {
      level: "instrument",
      label: getDisplayName("instrument")?.toUpperCase(),
    },
  ].filter(Boolean);

  const handleLogisticsBreadcrumbClick = (level) => {
    switch (level) {
      case "logistics":
        clearFromLevel("site");
        break;
      case "site":
        clearFromLevel("plant");
        break;
      case "plant":
        clearFromLevel("asset");
        break;
      case "asset":
        clearFromLevel("subAsset");
        break;
      case "subAsset":
        clearFromLevel("component");
        break;
      case "component":
        clearFromLevel("instrument");
        break;
      default:
        break;
    }
  };
  // ── Logistics breadcrumb support: end ─────────────────────────────

  // ── Design & Engineering Docs breadcrumb support: start ─────────────
  // GlobalDocuments lives at a fixed "/design-engineering-docs" URL, so
  // unlike every other page there's no pathname segment to derive a
  // hierarchy from. Instead we lean on breadcrumbs context — already kept
  // current by Sites/Plants/Equipment/PanelComponents/Instruments on
  // selection — and only show it when the page was actually reached with
  // nav context attached (see docsPath in DesktopNavigation.js), so a
  // direct/bookmarked visit with no query string just shows the plain
  // "Design & Engineering Docs" label instead of a possibly-stale trail.
  const isDocsRoute = location.pathname === "/design-engineering-docs";
  const hasDocsContext = isDocsRoute && location.search.length > 0;

  const docsTrail = [
    breadcrumbs.site && {
      segment: "sites",
      label: getDisplayName("site")?.toUpperCase(),
    },
    breadcrumbs.plant && {
      segment: "plants",
      label: getDisplayName("plant")?.toUpperCase(),
    },
    breadcrumbs.asset && {
      segment: "assets",
      label: getDisplayName("asset")?.toUpperCase(),
    },
    breadcrumbs.subAsset && {
      segment: "sub-asset",
      label: getDisplayName("subAsset")?.toUpperCase(),
    },
    breadcrumbs.component && {
      segment: "components",
      label: getDisplayName("component")?.toUpperCase(),
    },
    { segment: null, label: "DESIGN-ENGINEERING-DOCS" },
  ].filter(Boolean);
  // ── Design & Engineering Docs breadcrumb support: end ────────────────

  // ── Trail passed with the navigation: start ─────────────────────────
  // A page can pass its own trail as location state (navigate(to, { state: { breadcrumbTrail } })), as the
  // MaintenanceGuide does for the chosen asset's location. Nothing is saved, and the breadcrumbs context (the
  // site/plant/asset other pages share) is left alone. Items: { label, to? }; only the last has no link.
  const pageTrail = Array.isArray(location.state?.breadcrumbTrail)
    ? location.state.breadcrumbTrail.filter((item) => item && typeof item.label === "string" && item.label)
    : null;
  // ── Trail passed with the navigation: end ───────────────────────────

  // Map display indices back to original pathname indices
  const getOriginalIndex = (displayIndex) => {
    let originalIndex = -1;
    let displayCount = -1;

    for (let i = 0; i < pathnames.length; i++) {
      const segment = pathnames[i];
      if (!['siteid', 'plantid', 'assetid', 'subassetid', 'componentid', 'instrumentid', 'projectid'].includes(segment.toLowerCase()) &&
        !segment.match(/^\d+$/) && !UUID.test(segment)) {
        displayCount++;
      }
      if (displayCount === displayIndex) {
        originalIndex = i;
        break;
      }
    }

    return originalIndex;
  };

  // Function to handle breadcrumb clicks
  const handleBreadcrumbClick = (segment, displayIndex) => {
    // Set the selected item and clear downstream items FIRST
    switch (segment) {
      case "sites":
        if (breadcrumbs.site) {
          setSelectedItem('site', breadcrumbs.site);
        }
        clearFromLevel('plant');
        break;

      case "plants":
        if (breadcrumbs.plant) {
          setSelectedItem('plant', breadcrumbs.plant);
        }
        clearFromLevel('asset');
        break;

      case "assets":
        if (breadcrumbs.asset) {
          setSelectedItem('asset', breadcrumbs.asset);
        }
        clearFromLevel('subAsset');
        break;

      case "sub-asset":
        if (breadcrumbs.subAsset) {
          setSelectedItem('subAsset', breadcrumbs.subAsset);
        }
        break;
      case "components":
        if (breadcrumbs.component) {
          setSelectedItem('component', breadcrumbs.component);
        }
        break;
      case "instrument":
        if (breadcrumbs.instrument) {
          setSelectedItem('instrument', breadcrumbs.instrument);
        }
        break;

      case "project-dashboard":
        if (breadcrumbs.project) {
          setSelectedItem('project', breadcrumbs.project);
        }
        break;

      default:
        break;
    }

    // ✅ Use getRouteForSegment which properly builds the route with IDs
    const routeTo = getRouteForSegment(segment, displayIndex);

    navigate(routeTo);
  };

  // Function to build proper routes
  const getRouteForSegment = (segment, displayIndex) => {
    const originalIndex = getOriginalIndex(displayIndex);
    if (originalIndex === -1) return "/";

    const pathSegments = pathnames.slice(0, originalIndex + 1);

    // Build routes based on segment and breadcrumb data
    switch (segment) {
      case "sites":
        return breadcrumbs.site?.id ? `/sites/${breadcrumbs.site.id}` : "/sites";

      case "plants":
        // ✅ Include site ID in the plants route
        return breadcrumbs.site?.id && breadcrumbs.plant?.id
          ? `/sites/${breadcrumbs.site.id}/plants/${breadcrumbs.plant.id}`
          : breadcrumbs.site?.id
            ? `/sites/${breadcrumbs.site.id}/plants`
            : "/sites";

      case "assets":
        // ✅ Include site, plant, and asset IDs
        return breadcrumbs.site?.id && breadcrumbs.plant?.id && breadcrumbs.asset?.id
          ? `/sites/${breadcrumbs.site.id}/plants/${breadcrumbs.plant.id}/assets/${breadcrumbs.asset.id}`
          : breadcrumbs.site?.id && breadcrumbs.plant?.id
            ? `/sites/${breadcrumbs.site.id}/plants/${breadcrumbs.plant.id}/assets`
            : "/sites";

      case "sub-asset":
        // ✅ Include all parent IDs
        return breadcrumbs.site?.id && breadcrumbs.plant?.id && breadcrumbs.asset?.id && breadcrumbs.subAsset?.id
          ? `/sites/${breadcrumbs.site.id}/plants/${breadcrumbs.plant.id}/assets/${breadcrumbs.asset.id}/sub-asset/${breadcrumbs.subAsset.id}`
          : breadcrumbs.site?.id && breadcrumbs.plant?.id && breadcrumbs.asset?.id
            ? `/sites/${breadcrumbs.site.id}/plants/${breadcrumbs.plant.id}/assets/${breadcrumbs.asset.id}/sub-asset`
            : "/sites";

      case "components":
        // ✅ Include all parent IDs
        return breadcrumbs.site?.id && breadcrumbs.plant?.id && breadcrumbs.asset?.id && breadcrumbs.subAsset?.id && breadcrumbs.component?.id
          ? `/sites/${breadcrumbs.site.id}/plants/${breadcrumbs.plant.id}/assets/${breadcrumbs.asset.id}/sub-asset/${breadcrumbs.subAsset.id}/components/${breadcrumbs.component.id}`
          : breadcrumbs.site?.id && breadcrumbs.plant?.id && breadcrumbs.asset?.id && breadcrumbs.subAsset?.id
            ? `/sites/${breadcrumbs.site.id}/plants/${breadcrumbs.plant.id}/assets/${breadcrumbs.asset.id}/sub-asset/${breadcrumbs.subAsset.id}/components`
            : breadcrumbs.site?.id && breadcrumbs.plant?.id && breadcrumbs.asset?.id
              ? `/sites/${breadcrumbs.site.id}/plants/${breadcrumbs.plant.id}/assets/${breadcrumbs.asset.id}`
              : breadcrumbs.site?.id && breadcrumbs.plant?.id ? `/sites/${breadcrumbs.site.id}/plants/${breadcrumbs.plant.id}`
                : breadcrumbs.site?.id
                  ? `/sites/${breadcrumbs.site.id}/plants`
                  : "/sites";

      case "project-dashboard":
        return breadcrumbs.site?.id && breadcrumbs.project?.id
          ? `/sites/${breadcrumbs.site.id}/project-dashboard/${breadcrumbs.project.id}/open`
          : breadcrumbs.site?.id
            ? `/sites/${breadcrumbs.site.id}/project-dashboard`
            : "/sites";

      default:
        return `/${pathSegments.join("/")}`;
    }
  };

  return (
    <Box component="nav" aria-label="breadcrumb" sx={{ flexGrow: 1, p: 3 }}>
      <Link
        to="/"
        onMouseEnter={() => setHoveredLink("home")}
        onMouseLeave={() => setHoveredLink(null)}
        onClick={() => clearFromLevel('site')}
        style={{
          textDecoration: "none",
          color: "#3498DB",
          fontSize: "16px",
          padding: "2px 5px",
          borderRadius: "3px",
          backgroundColor: hoveredLink === "home" ? "#D6EAF8" : "transparent",
        }}
      >
        <HomeIcon fontSize="small" />
      </Link>

      {/* Logistics breadcrumb rendering: start */}
      {pageTrail?.length ? pageTrail.map((item, index) => {
        const isLast = index === pageTrail.length - 1;
        return (
          <React.Fragment key={`trail-${index}`}>
            <span style={{ fontSize: "16px" }}> / </span>
            {isLast || !item.to ? (
              <span style={{ fontSize: isLast ? "17px" : "16px" }}>{item.label}</span>
            ) : (
              <Link
                to={item.to}
                onMouseEnter={() => setHoveredLink(`trail-${index}`)}
                onMouseLeave={() => setHoveredLink(null)}
                style={{
                  textDecoration: "none",
                  color: "#3498DB",
                  fontSize: "16px",
                  padding: "2px 5px",
                  borderRadius: "3px",
                  backgroundColor: hoveredLink === `trail-${index}` ? "#D6EAF8" : "transparent",
                }}
              >
                {item.label}
              </Link>
            )}
          </React.Fragment>
        );
      }) : isLogisticsRoute ? logisticsTrail.map((item, displayIndex) => {
        const isLast = displayIndex === logisticsTrail.length - 1;
        const routeTo = buildLogisticsRoute(item.level);

        return (
          <React.Fragment key={item.level}>
            <span style={{ fontSize: "16px" }}> / </span>

            {isLast ? (
              <span style={{ fontSize: "17px" }}>{item.label}</span>
            ) : (
              <Link
                to={routeTo}
                onMouseEnter={() => setHoveredLink(`logistics-${item.level}`)}
                onMouseLeave={() => setHoveredLink(null)}
                onClick={(e) => {
                  e.preventDefault();
                  handleLogisticsBreadcrumbClick(item.level);
                  navigate(routeTo);
                }}
                style={{
                  textDecoration: "none",
                  color: "#3498DB",
                  fontSize: "16px",
                  padding: "2px 5px",
                  borderRadius: "3px",
                  backgroundColor:
                    hoveredLink === `logistics-${item.level}`
                      ? "#D6EAF8"
                      : "transparent",
                }}
              >
                {item.label}
              </Link>
            )}
          </React.Fragment>
        );
      }) : hasDocsContext ? docsTrail.map((item, displayIndex) => {
        const isLast = displayIndex === docsTrail.length - 1;
        // displayIndex is unused by getRouteForSegment/handleBreadcrumbClick
        // for these named segments (sites/plants/assets/sub-asset/
        // components) — they resolve routes purely from breadcrumbs — so a
        // fixed 0 is safe here; it only needs to satisfy the "is this a
        // known display position" guard inside getRouteForSegment.
        const routeTo = item.segment ? getRouteForSegment(item.segment, 0) : null;

        return (
          <React.Fragment key={item.segment ?? "docs-current"}>
            <span style={{ fontSize: "16px" }}> / </span>

            {isLast ? (
              <span style={{ fontSize: "17px" }}>{item.label}</span>
            ) : (
              <Link
                to={routeTo}
                onMouseEnter={() => setHoveredLink(`docs-${item.segment}`)}
                onMouseLeave={() => setHoveredLink(null)}
                onClick={(e) => {
                  e.preventDefault();
                  handleBreadcrumbClick(item.segment, 0);
                }}
                style={{
                  textDecoration: "none",
                  color: "#3498DB",
                  fontSize: "16px",
                  padding: "2px 5px",
                  borderRadius: "3px",
                  backgroundColor:
                    hoveredLink === `docs-${item.segment}`
                      ? "#D6EAF8"
                      : "transparent",
                }}
              >
                {item.label}
              </Link>
            )}
          </React.Fragment>
        );
      }) : displayPathnames.map((segment, displayIndex) => {
        const isLast = displayIndex === displayPathnames.length - 1;
        const routeTo = getRouteForSegment(segment, displayIndex);
        const label = labelMap[segment] || segment.charAt(0).toUpperCase() + segment.slice(1);

        return (
          <React.Fragment key={displayIndex}>
            <span style={{ fontSize: "16px" }}> / </span>

            {isLast ? (
              <span style={{ fontSize: "17px" }}>{label}</span>
            ) : (
              <Link
                to={routeTo}
                onMouseEnter={() => setHoveredLink(displayIndex)}
                onMouseLeave={() => setHoveredLink(null)}
                onClick={(e) => {
                  e.preventDefault();
                  handleBreadcrumbClick(segment, displayIndex);
                }}
                style={{
                  textDecoration: "none",
                  color: "#3498DB",
                  fontSize: "16px",
                  padding: "2px 5px",
                  borderRadius: "3px",
                  backgroundColor: hoveredLink === displayIndex ? "#D6EAF8" : "transparent",
                }}
              >
                {label}
              </Link>
            )}
          </React.Fragment>
        );
      })}
      {/* Logistics breadcrumb rendering: end */}
    </Box>
  );
}

export default BasicBreadcrumbs;